import { useCallback, useEffect, useState } from 'react';

export const ACTIVE_PROJECT_STORAGE_KEY = 'osnova:active-project-id';
export const ACTIVE_PROJECT_NAME_STORAGE_KEY = 'osnova:active-project-name';
export const ACTIVE_CONTRACT_STORAGE_KEY = 'osnova:active-contract-id';
const CONTEXT_EVENT_NAME = 'osnova:active-context-change';

export function getActiveProjectId(): string {
  try {
    return window.localStorage.getItem(ACTIVE_PROJECT_STORAGE_KEY) ?? '';
  } catch {
    return '';
  }
}

export function getActiveProjectName(): string {
  try {
    return window.localStorage.getItem(ACTIVE_PROJECT_NAME_STORAGE_KEY) ?? '';
  } catch {
    return '';
  }
}

export function setActiveProjectId(
  projectId: string | null | undefined,
  meta?: { name?: string | null; code?: string | null },
): void {
  try {
    const val = (projectId ?? '').trim();
    if (val) {
      const prevId = window.localStorage.getItem(ACTIVE_PROJECT_STORAGE_KEY) ?? '';
      window.localStorage.setItem(ACTIVE_PROJECT_STORAGE_KEY, val);
      if (meta?.name !== undefined) {
        const nameVal = (meta.name ?? '').trim();
        if (nameVal) {
          window.localStorage.setItem(ACTIVE_PROJECT_NAME_STORAGE_KEY, nameVal);
        } else {
          window.localStorage.removeItem(ACTIVE_PROJECT_NAME_STORAGE_KEY);
        }
      } else if (prevId !== val) {
        window.localStorage.removeItem(ACTIVE_PROJECT_NAME_STORAGE_KEY);
      }
    } else {
      window.localStorage.removeItem(ACTIVE_PROJECT_STORAGE_KEY);
      window.localStorage.removeItem(ACTIVE_PROJECT_NAME_STORAGE_KEY);
    }
    window.dispatchEvent(new CustomEvent(CONTEXT_EVENT_NAME));
  } catch {
    // ignore storage errors
  }
}

export function getActiveContractId(): string {
  try {
    return window.localStorage.getItem(ACTIVE_CONTRACT_STORAGE_KEY) ?? '';
  } catch {
    return '';
  }
}

export function setActiveContractId(contractId: string | null | undefined): void {
  try {
    const val = (contractId ?? '').trim();
    if (val) {
      window.localStorage.setItem(ACTIVE_CONTRACT_STORAGE_KEY, val);
    } else {
      window.localStorage.removeItem(ACTIVE_CONTRACT_STORAGE_KEY);
    }
    window.dispatchEvent(new CustomEvent(CONTEXT_EVENT_NAME));
  } catch {
    // ignore storage errors
  }
}

export function useActiveProjectContext() {
  const [activeProjectId, setProjectIdState] = useState<string>(() => getActiveProjectId());
  const [activeProjectName, setProjectNameState] = useState<string>(() => getActiveProjectName());
  const [activeContractId, setContractIdState] = useState<string>(() => getActiveContractId());

  useEffect(() => {
    const sync = () => {
      setProjectIdState(getActiveProjectId());
      setProjectNameState(getActiveProjectName());
      setContractIdState(getActiveContractId());
    };
    window.addEventListener('storage', sync);
    window.addEventListener(CONTEXT_EVENT_NAME, sync);
    return () => {
      window.removeEventListener('storage', sync);
      window.removeEventListener(CONTEXT_EVENT_NAME, sync);
    };
  }, []);

  const updateActiveProjectId = useCallback(
    (id: string | null | undefined, meta?: { name?: string | null; code?: string | null }) => {
      setActiveProjectId(id, meta);
      setProjectIdState((id ?? '').trim());
      if (!id || !(id ?? '').trim()) {
        setProjectNameState('');
      } else if (meta?.name !== undefined) {
        setProjectNameState((meta.name ?? '').trim());
      }
    },
    [],
  );

  const updateActiveContractId = useCallback((id: string | null | undefined) => {
    setActiveContractId(id);
    setContractIdState((id ?? '').trim());
  }, []);

  return {
    activeProjectId,
    activeProjectName,
    activeContractId,
    setActiveProjectId: updateActiveProjectId,
    setActiveProject: updateActiveProjectId,
    setActiveContractId: updateActiveContractId,
  };
}
